import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Select from "react-select";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  COMPANY_NAME,
  ChartOfAccountStyles,
  firstDayOfMonth,
  formatDate,
  lastDayOfMonth,
  money,
  moneyOrBlank,
  num,
  isDebit,
  isOpeningRow,
  ledgerSummary,
} from "./ChartOfAccountShared";

// Also serves /chart-of-account-ledger-report, where the account comes from
// ?id= and may not be picked yet.
const Ledger = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = params.id || searchParams.get("id") || "";
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [chartOfAccount, setChartOfAccount] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [alert, setAlert] = useState(null);
  const [inputs, setInputs] = useState({
    start_date: firstDayOfMonth(),
    end_date: lastDayOfMonth(),
  });
  const [loading, setLoading] = useState(!!id);
  // Only the latest request may fill the page when accounts are switched
  // quickly.
  const requestRef = useRef(0);

  useEffect(() => {
    Http.get("/chartOfAccountSelect2")
      .then((res) => setAccounts(res.data || []))
      .catch((error) => console.error("Fetch error:", error));
  }, []);

  const selectAccount = (option) => {
    if (!option || String(option.value) === String(id)) return;
    if (params.id) {
      navigate(`/chart-of-account-ledger/${option.value}`);
    } else {
      setSearchParams({ id: option.value });
    }
  };

  const selectedAccount =
    accounts.find((option) => String(option.value) === String(id)) || null;

  const buildUrl = () => {
    let url = `/report/chart-of-account-ledger?id=${id}`;
    if (inputs.start_date) {
      url += `&start_date=${inputs.start_date}`;
    }
    if (inputs.end_date) {
      url += `&end_date=${inputs.end_date}`;
    }
    return url;
  };

  const fetchData = async () => {
    const request = ++requestRef.current;
    if (!id) {
      setReceiveData([]);
      setChartOfAccount({});
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const res = await Http.get(buildUrl());
      if (request !== requestRef.current) return;
      setReceiveData(res.data.list || []);
      setChartOfAccount(res.data.chart_of_account || {});
    } catch (error) {
      if (request !== requestRef.current) return;
      console.error("Fetch error:", error);
      setReceiveData([]);
      setChartOfAccount({});
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const summary = useMemo(
    () =>
      ledgerSummary(
        receiveData,
        (row) => num(row.debit),
        (row) => num(row.credit),
      ),
    [receiveData],
  );

  const dateBodyTemplate = (rowData) =>
    isOpeningRow(rowData) ? (
      <span style={{ fontWeight: 600, color: "#2a2962" }}>Opening balance</span>
    ) : (
      <span style={{ whiteSpace: "nowrap" }}>{formatDate(rowData.date)}</span>
    );

  const codeBodyTemplate = (rowData) =>
    rowData.chart_of_account ? (
      <span className="coa-code">{rowData.chart_of_account}</span>
    ) : (
      <span style={{ color: "#b6bacb" }}>—</span>
    );

  const drCrBodyTemplate = (rowData) => {
    if (!rowData.dr_cr) {
      return <span className="coa-pill coa-pill-muted">—</span>;
    }
    const debit = isDebit(rowData.dr_cr);
    return (
      <span className={`coa-pill ${debit ? "coa-pill-dr" : "coa-pill-cr"}`}>
        {debit ? "DR" : "CR"}
      </span>
    );
  };

  const amountBodyTemplate = (field) => {
    const Component = (rowData) => {
      const text = moneyOrBlank(rowData[field]);
      return (
        <span className="coa-num" style={{ display: "block" }}>
          {text || <span style={{ color: "#dfe2ec" }}>—</span>}
        </span>
      );
    };
    Component.displayName = `AmountBodyTemplate(${field})`;
    return Component;
  };

  const balanceBodyTemplate = (rowData) => {
    const value = num(
      isOpeningRow(rowData) ? rowData.opening_balance : rowData.balance,
    );
    return (
      <span
        className="coa-num"
        style={{
          display: "block",
          fontWeight: 700,
          color: value < 0 ? "#e42320" : "#2a2962",
        }}
      >
        {money(value)}
      </span>
    );
  };

  const textBodyTemplate = (field) => {
    const Component = (rowData) =>
      rowData[field] ? (
        <span>{rowData[field]}</span>
      ) : (
        <span style={{ color: "#b6bacb" }}>—</span>
      );
    Component.displayName = `TextBodyTemplate(${field})`;
    return Component;
  };

  // The line's own remark; older lines have none, so they fall back to the
  // voucher's remark, greyed to tell the two apart.
  const remarksBodyTemplate = (rowData) =>
    rowData.remarks ? (
      <span>{rowData.remarks}</span>
    ) : rowData.voucher_remarks ? (
      <span style={{ color: "#8b90a5" }} title="Voucher remarks">
        {rowData.voucher_remarks}
      </span>
    ) : (
      <span style={{ color: "#b6bacb" }}>—</span>
    );

  const escapeHtml = (text) =>
    String(text ?? "").replace(
      /[&<>"]/g,
      (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch],
    );

  const cols = [
    { field: "date", header: "Date", body: dateBodyTemplate },
    { field: "chart_of_account", header: "Code", body: codeBodyTemplate },
    {
      field: "serial_number",
      header: "No",
      body: textBodyTemplate("serial_number"),
    },
    { field: "type", header: "Voucher Type", body: textBodyTemplate("type") },
    { field: "dr_cr", header: "Type", body: drCrBodyTemplate },
    {
      field: "subsidiary",
      header: "Subsidiary",
      body: textBodyTemplate("subsidiary"),
    },
    { field: "remarks", header: "Remarks", body: remarksBodyTemplate },
    {
      field: "debit",
      header: "Dr. Amount",
      body: amountBodyTemplate("debit"),
      headerClassName: "coa-num",
    },
    {
      field: "credit",
      header: "Cr. Amount",
      body: amountBodyTemplate("credit"),
      headerClassName: "coa-num",
    },
    {
      field: "balance",
      header: "Balance",
      body: balanceBodyTemplate,
      headerClassName: "coa-num",
    },
  ];

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Chart Of Account Ledger Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #22242f; }
          .header { text-align: center; margin-bottom: 24px; border-bottom: 2px solid #2a2962; padding-bottom: 14px; }
          .company { margin: 0; font-size: 22px; font-weight: bold; color: #2a2962; }
          .account { margin: 8px 0 4px; font-size: 16px; }
          .period { margin: 0; font-size: 13px; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background-color: #2a2962; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 11px; }
          tfoot td { background-color: #f2f3f9; font-weight: bold; }
          .text-right { text-align: right; font-variant-numeric: tabular-nums; }
          .text-center { text-align: center; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="company">${COMPANY_NAME}</h1>
          <h3 class="account">${chartOfAccount.code || ""} - ${
            chartOfAccount.name || "Chart of Account"
          }</h3>
          <p class="period">Statement from ${inputs.start_date} to ${
            inputs.end_date
          }</p>
        </div>
        <table>
          <thead>
            <tr>
              ${cols.map((col) => `<th>${col.header}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${receiveData
              .map(
                (row) => `
              <tr>
                <td class="text-center">${
                  isOpeningRow(row) ? "Opening balance" : row.date || ""
                }</td>
                <td>${row.chart_of_account || ""}</td>
                <td class="text-center">${row.serial_number || ""}</td>
                <td>${row.type || ""}</td>
                <td class="text-center">${
                  row.dr_cr ? (isDebit(row.dr_cr) ? "DR" : "CR") : ""
                }</td>
                <td>${row.subsidiary || ""}</td>
                <td${row.remarks ? "" : ' style="color:#777"'}>${escapeHtml(
                  row.remarks || row.voucher_remarks,
                )}</td>
                <td class="text-right">${moneyOrBlank(row.debit)}</td>
                <td class="text-right">${moneyOrBlank(row.credit)}</td>
                <td class="text-right">${money(
                  isOpeningRow(row) ? row.opening_balance : row.balance,
                )}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="7" class="text-right">Opening balance</td>
              <td colspan="2"></td>
              <td class="text-right">${money(summary.opening)}</td>
            </tr>
            <tr>
              <td colspan="7" class="text-right">Period total</td>
              <td class="text-right">${money(summary.debit)}</td>
              <td class="text-right">${money(summary.credit)}</td>
              <td class="text-right">${money(summary.closing)}</td>
            </tr>
          </tfoot>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const movement = summary.debit - summary.credit;

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Account Ledger{" "}
              {chartOfAccount.code && (
                <span className="coa-code">{chartOfAccount.code}</span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {chartOfAccount.name ||
                (id ? `Account #${id}` : "No account selected")}{" "}
              &middot; {formatDate(inputs.start_date)} —{" "}
              {formatDate(inputs.end_date)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            {params.id ? (
              <Link
                to="/chart-of-account"
                className="btn btn-outline-secondary"
              >
                <i className="fa fa-arrow-left mg-r-6"></i>Back to List
              </Link>
            ) : (
              <Link to="/reports" className="btn btn-outline-secondary">
                <i className="fa fa-arrow-left mg-r-6"></i>Reports
              </Link>
            )}
            {id && (
              <Link
                to={`/edit-chart-of-account/${id}`}
                className="btn btn-outline-secondary"
              >
                <i className="fa fa-pencil mg-r-6"></i>Edit Account
              </Link>
            )}
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !receiveData.length}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Account &amp; Period
              </span>
              {id && (
                <span className="coa-head-note">
                  {summary.count} {summary.count === 1 ? "entry" : "entries"} in
                  range
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-end">
                <div className="col-lg-4">
                  <label>Chart of Account</label>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    options={accounts}
                    value={selectedAccount}
                    onChange={selectAccount}
                    isLoading={!accounts.length}
                    placeholder="Search by code or name..."
                    noOptionsMessage={() => "No matching account"}
                    aria-label="Chart of Account"
                  />
                </div>
                <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date}
                    onChange={handleInputChange}
                    max={inputs.end_date}
                  />
                </div>
                <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date}
                    onChange={handleInputChange}
                    min={inputs.start_date}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <button
                    className="btn btn-primary"
                    onClick={fetchData}
                    disabled={loading || !id}
                  >
                    <i
                      className={`fa ${
                        loading ? "fa-spinner fa-spin" : "fa-search"
                      } mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!id ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <i className="fa fa-book"></i>
                Pick a chart of account above to see its ledger.
              </div>
            </div>
          ) : (
            <>
              <div className="row mg-b-20">
                <div className="col-md-3 mg-b-10">
                  <div className="coa-stat">
                    <i className="fa fa-flag-o coa-stat-icon"></i>
                    <span className="coa-stat-label">Opening Balance</span>
                    <span className="coa-stat-value">
                      {money(summary.opening)}
                    </span>
                    <span className="coa-stat-sub">
                      As at {formatDate(inputs.start_date)}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div className="coa-stat">
                    <i className="fa fa-arrow-circle-o-down coa-stat-icon"></i>
                    <span className="coa-stat-label">Total Debit</span>
                    <span className="coa-stat-value">
                      {money(summary.debit)}
                    </span>
                    <span className="coa-stat-sub">Booked in this period</span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div className="coa-stat">
                    <i className="fa fa-arrow-circle-o-up coa-stat-icon"></i>
                    <span className="coa-stat-label">Total Credit</span>
                    <span className="coa-stat-value">
                      {money(summary.credit)}
                    </span>
                    <span className="coa-stat-sub">Booked in this period</span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div
                    className={`coa-stat ${
                      summary.closing < 0 ? "is-alert" : "is-ok"
                    }`}
                  >
                    <i className="fa fa-balance-scale coa-stat-icon"></i>
                    <span className="coa-stat-label">Closing Balance</span>
                    <span className="coa-stat-value">
                      {money(summary.closing)}
                    </span>
                    <span className="coa-stat-sub">
                      {movement >= 0 ? "Net debit " : "Net credit "}
                      {money(Math.abs(movement))} this period
                    </span>
                  </div>
                </div>
              </div>

              <div className="coa-panel">
                <div className="coa-panel-head">
                  <span>
                    <i className="fa fa-table me-2"></i>Ledger Entries
                  </span>
                </div>
                <div className="coa-panel-body">
                  <ReactDataTable
                    cols={cols}
                    receiveData={receiveData}
                    isLoading={loading}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ledger;
